import { CANCEL } from 'redux-saga';

/**
 * Suspends the enrichment polls while the tab is hidden.
 *
 * Every poll here re-reads a server-side payload far larger than the response it
 * produces, so a tab nobody is looking at is pure waste. Chrome does throttle
 * hidden-tab timers to roughly one tick a minute, and eventually freezes or
 * discards the tab outright; but those are heuristics, not guarantees: their
 * thresholds move with the Chrome version, the user's Memory Saver settings and
 * the machine's memory pressure, and a tab the user keeps returning to is
 * precisely the one Chrome avoids discarding. Leaving the decision to the
 * browser left a residual band of traffic we neither saw nor controlled.
 *
 * Note that `hidden` is not `unfocused`: a tab on a second monitor, or in a
 * window that is merely not in front, stays visible and keeps polling at full
 * rate. That is deliberate; someone is watching it.
 */

/**
 * Resolves at once when the page is visible, otherwise on the next transition
 * back to visible. Carries a redux-saga `CANCEL` hook so a cancelled poll (page
 * unmount, `sessionsStopped`) detaches the listener instead of leaking it.
 *
 * The hook is attached with `defineProperty` rather than assigned: redux-saga
 * types `CANCEL` as a plain `string`, so declaring `{ [CANCEL]: ... }` would
 * degrade the promise type into a string index signature.
 */
export function whenPageVisible(): Promise<void> {
  if (typeof document === 'undefined' || !document.hidden) return Promise.resolve();

  let detach = () => {};
  const waiting = new Promise<void>((resolve) => {
    const onVisibilityChange = () => {
      if (document.hidden) return;
      detach();
      resolve();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    detach = () => document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  Object.defineProperty(waiting, CANCEL, { value: () => detach() });
  return waiting;
}
